import type {
  InputHTMLAttributes,
  ReactNode,
  SelectHTMLAttributes,
  TextareaHTMLAttributes,
} from "react";
import { useId } from "react";

import { Select, type SelectOption } from "@/components/ui/Select";
import { tone, type Tone } from "@/components/ui/tone";
import { cn } from "@/lib/cn";

function control(t: ReturnType<typeof tone>): string {
  return cn(
    "w-full rounded-xl border px-3.5 py-2.5 text-sm transition-colors",

    "min-h-11",
    t.control,
    "placeholder:opacity-60 disabled:cursor-not-allowed disabled:opacity-60",
    "aria-[invalid=true]:border-danger",
  );
}

type Shared = {
  label: string;
  hint?: string | undefined;
  error?: string | undefined;

  hideLabel?: boolean | undefined;
  tone?: Tone | undefined;
};

export function FieldShell({
  label,
  hint,
  error,
  hideLabel,
  tone: which,
  htmlFor,
  describedBy,
  children,
}: Shared & { htmlFor: string; describedBy?: string | undefined; children: ReactNode }) {
  const t = tone(which);
  const invalid = error !== undefined && error !== "";
  return (
    <div className="flex flex-col gap-1.5">
      <label
        className={cn("text-sm font-medium", t.text, hideLabel === true && "sr-only")}
        htmlFor={htmlFor}
      >
        {label}
      </label>
      {children}
      {invalid ? (
        <p className="text-xs text-danger" id={describedBy} role="alert">
          {error}
        </p>
      ) : hint !== undefined ? (
        <p className={cn("text-xs", t.muted)} id={describedBy}>
          {hint}
        </p>
      ) : null}
    </div>
  );
}

export type TextFieldProps = Shared & InputHTMLAttributes<HTMLInputElement>;

export function TextField({
  label,
  hint,
  error,
  hideLabel,
  tone: which,
  className,
  ...rest
}: TextFieldProps) {
  const id = useId();
  const invalid = error !== undefined && error !== "";
  return (
    <FieldShell
      describedBy={`${id}-help`}
      error={error}
      hideLabel={hideLabel}
      hint={hint}
      htmlFor={id}
      label={label}
      tone={which}
    >
      <input
        aria-describedby={hint === undefined && !invalid ? undefined : `${id}-help`}
        aria-invalid={invalid}
        className={cn(control(tone(which)), className)}
        id={id}
        {...rest}
      />
    </FieldShell>
  );
}

export type TextAreaFieldProps = Shared & TextareaHTMLAttributes<HTMLTextAreaElement>;

export function TextAreaField({
  label,
  hint,
  error,
  hideLabel,
  tone: which,
  className,
  ...rest
}: TextAreaFieldProps) {
  const id = useId();
  const invalid = error !== undefined && error !== "";
  return (
    <FieldShell
      describedBy={`${id}-help`}
      error={error}
      hideLabel={hideLabel}
      hint={hint}
      htmlFor={id}
      label={label}
      tone={which}
    >
      <textarea
        aria-describedby={hint === undefined && !invalid ? undefined : `${id}-help`}
        aria-invalid={invalid}
        className={cn(control(tone(which)), "min-h-24 resize-y", className)}
        id={id}
        {...rest}
      />
    </FieldShell>
  );
}

export type SelectFieldProps = Shared &
  Omit<SelectHTMLAttributes<HTMLSelectElement>, "onChange" | "value" | "size"> & {
    options: SelectOption[];
    value: string;
    onChange: (value: string) => void;
    placeholder?: string | undefined;

    searchable?: boolean | undefined;
  };

export function SelectField({
  label,
  hint,
  error,
  hideLabel,
  tone: which,
  options,
  value,
  onChange,
  placeholder,
  className,
  disabled,
  name,
  searchable,
}: SelectFieldProps) {
  const id = useId();
  const invalid = error !== undefined && error !== "";
  const describedBy = hint === undefined && !invalid ? undefined : `${id}-help`;
  return (
    <FieldShell
      describedBy={`${id}-help`}
      error={error}
      hideLabel={hideLabel}
      hint={hint}
      htmlFor={id}
      label={label}
      tone={which}
    >
      <Select
        className={className}
        describedBy={describedBy}
        disabled={disabled}
        id={id}
        invalid={invalid}
        name={name}
        onChange={onChange}
        options={options}
        placeholder={placeholder}
        searchable={searchable}
        tone={which}
        value={value}
      />
    </FieldShell>
  );
}

export type { SelectOption };
